import { formatDateRelative } from '@nucleus/lib-datetime';
import { compose } from '@nucleus/lib-function';
import { MediaListItem, SermonPreview } from '@nucleus/sermon-theme-elements';
import {
  FontPaletteKey,
  ThemeActionElement,
  ThemeCardElement,
  ThemeCustomElement,
  ThemeImageElement,
  ThemeLinkElement,
  ThemeParameterElement,
  ThemeSermonPreviewElement,
  ThemeTextAlign,
  ThemeVideoElement,
} from '@nucleus/types/web';
import {
  RichText,
  RichTextCustomRichTextComponentMap,
  RichTextElementComponentMap,
  RichTextRegistryProvider,
  useRichTextParameters,
} from '@nucleus/web-theme';
import { Text, WebImage, buildTransparentColorFromCssVariable } from '@nucleus/web-theme-elements';
import React from 'react';
import ReactPlayer from 'react-player';
import { Link as NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { SectionButton } from '../components/Button';
import { cornerStyleInterpolationFactory } from '../lib/cornerStyle';
import { formatSrcSet } from '../lib/richtext';

type PropsWithNode<T = {}> = T & { node: ThemeCustomElement };

const getAlignStyle = (align: ThemeTextAlign | undefined): React.CSSProperties | undefined =>
  align !== undefined ? { textAlign: align } : undefined;

const withAlignStyle = <C extends React.ComponentType<any>>(
  Component: C
): C extends React.ComponentType<infer P> ? React.ComponentType<PropsWithNode<P>> : C => {
  const WithAlignStyle = ({ node, ...props }: PropsWithNode) => {
    // @ts-expect-error - Generic C always resolves to styled-components or HTML elements that accept style
    return <Component node={node} {...props} style={getAlignStyle(node.align)} />;
  };

  WithAlignStyle.displayName = `withAlignStyle(${getDisplayName(Component)})`;

  return WithAlignStyle as any;
};

const withNodeFontStyleProp = <C extends React.ComponentType<any>>(
  Component: C
): C extends React.ComponentType<infer P> ? React.ComponentType<PropsWithNode<P>> : C => {
  const WithNodeFontStyleProp = ({ node, ...props }: PropsWithNode) => {
    // @ts-expect-error - Generic C always resolves to styled-components or HTML elements that accept style
    return <Component fontStyle={node.fontStyle} {...props} />;
  };

  WithNodeFontStyleProp.displayName = `withNodeFontStyleProp(${getDisplayName(Component)})`;

  return WithNodeFontStyleProp as any;
};

const createStyledElement = <C extends React.ElementType, S extends FontPaletteKey>(Component: C, style?: S) =>
  compose(withAlignStyle, withNodeFontStyleProp)(Text.withFontStyle(Component, style));

const getDisplayName = (Component: React.ElementType): string =>
  (Component as React.ComponentType).displayName ?? (Component as React.ComponentType).name ?? Component;

const Image = ({
  node,
}: {
  children: React.ReactNode;
  node: ThemeImageElement & { link: Partial<ThemeLinkElement> };
}) => {
  if (node.link?.href !== undefined && node.link?.href !== '') {
    return (
      <Link node={node?.link as ThemeLinkElement}>
        <WebImage
          alt={node.alt}
          blurHash={node.blurHash}
          dimensions={node.dimensions}
          size={node.size}
          src={node.src}
          srcSet={formatSrcSet(node.srcSet)}
        />
      </Link>
    );
  }

  return (
    <WebImage
      alt={node.alt}
      blurHash={node.blurHash}
      dimensions={node.dimensions}
      size={node.size}
      src={node.src}
      srcSet={formatSrcSet(node.srcSet)}
    />
  );
};

const Video = ({ node }: { node: ThemeVideoElement }) => {
  return (
    <VideoContainer data-block-type="video">
      <StyledReactPlayer src={node.url} width="100%" height="100%" controls={true} playsInline={true} />
    </VideoContainer>
  );
};

const Parameter = ({ node }: { node: ThemeParameterElement }) => {
  const parameters = useRichTextParameters();
  return <>{parameters?.[node.parameter] ?? node.fallback}</>;
};

const Link = ({ children, node }: { children: React.ReactNode; node: ThemeLinkElement }) => {
  const anchorProps = node.openInNewTab === true ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  const isNativeLink = typeof window === 'undefined' || isUrl(node.href) || isApiPath(node.href);

  if (node.buttonStyle !== undefined) {
    const alignStyle = getAlignStyle(node.align);
    const linkProps = isNativeLink ? { href: node.href } : { to: node.href };

    return (
      <StyledButtonWrapper data-block-type="button" style={alignStyle}>
        <StyledSectionButton displayAs={node.buttonStyle} {...linkProps} {...anchorProps}>
          {children}
        </StyledSectionButton>
      </StyledButtonWrapper>
    );
  }

  if (isNativeLink) {
    return (
      <a data-node-type="link" href={node.href} {...anchorProps}>
        {children}
      </a>
    );
  }

  return (
    <NavLink data-node-type="link" {...anchorProps} to={node?.href}>
      {children}
    </NavLink>
  );
};

const StyledButtonWrapper = styled.div``;

const StyledSectionButton = styled(SectionButton)`
  display: inline-block;
  max-width: 589px;
  width: 100%;
`;

const Card = ({ node }: { children?: React.ReactNode; node: ThemeCardElement }) => {
  return (
    <div data-block-type="card">
      <StyledMediaListItem image={node.image} to={node.href}>
        {node.overline !== undefined && (
          <ListItemOverline style={{ marginBottom: '1em' }}>{node.overline}</ListItemOverline>
        )}
        {node.title !== undefined && (
          <ListItemHeadline>
            {node.icon === 'list' ? (
              <TitleRow>
                <IconList height="24" width="24" />
                {node.title}
              </TitleRow>
            ) : (
              node.title
            )}
          </ListItemHeadline>
        )}
        {node.description !== undefined && (
          <div style={{ maxWidth: '60%' }}>
            <ClampedP4>
              <LineClamp lines={3}>{node.description}</LineClamp>
            </ClampedP4>
          </div>
        )}
      </StyledMediaListItem>
    </div>
  );
};

const Sermon = ({ node }: { children?: React.ReactNode; node: ThemeSermonPreviewElement }) => {
  const formattedDate = node.date !== undefined ? formatDateRelative(node.date.split('Z')[0]) : undefined;

  const titleElement =
    node.title !== undefined ? (
      node.href !== undefined ? (
        <SermonMetaLink to={node.href}>
          <ClampedH5 style={{ maxWidth: '85%' }}>{node.title}</ClampedH5>
        </SermonMetaLink>
      ) : (
        <ClampedH5 style={{ maxWidth: '85%' }}>{node.title}</ClampedH5>
      )
    ) : undefined;

  const authorElement =
    node.authorName !== undefined ? (
      node.authorHref !== undefined ? (
        <SermonMetaLink to={node.authorHref}>{node.authorName}</SermonMetaLink>
      ) : (
        <>{node.authorName}</>
      )
    ) : undefined;

  const descriptionElement =
    node.description !== undefined ? (
      <DescriptionContainer style={{ maxWidth: '70%' }}>
        <LineClamp lines={2}>
          <RichTextRegistryProvider registerElements={{ paragraph: DescriptionParagraph }} registerText={{}}>
            <RichText nodes={node.description} />
          </RichTextRegistryProvider>
        </LineClamp>
      </DescriptionContainer>
    ) : undefined;

  const bylineElement =
    authorElement !== undefined || formattedDate !== undefined ? (
      <ClampedL6>
        {authorElement}
        {authorElement !== undefined && formattedDate !== undefined ? ' \u2022 ' : null}
        {formattedDate}
      </ClampedL6>
    ) : undefined;

  return (
    <SermonPreview
      title={titleElement}
      description={descriptionElement}
      byline={bylineElement}
      videoUrl={node.videoUrl}
      image={node.image}
    />
  );
};

const ActionLink = ({ children, node }: { children: React.ReactNode; node: ThemeActionElement }) => {
  const href = node.destination ?? '#';
  const anchorProps = node.openInNewTab === true ? { target: '_blank', rel: 'noreferrer' } : {};

  if (typeof window === 'undefined' || isUrl(href)) {
    return (
      <a href={href} {...anchorProps}>
        {children}
      </a>
    );
  }

  return (
    <NavLink {...anchorProps} to={href}>
      {children}
    </NavLink>
  );
};

const isUrl = (maybeUrl: string): boolean => {
  try {
    const url = new URL(maybeUrl);
    return url.toString() !== undefined;
  } catch (error) {
    return false;
  }
};

const isApiPath = (path: string): boolean => path.startsWith('/_api/');

const StyledMark = styled.mark`
  border-radius: 1em 0 1em 0;
  background-color: transparent;
  background-image: linear-gradient(
    -100deg,
    ${buildTransparentColorFromCssVariable('--color-highlight-background2', 0.3)},
    ${buildTransparentColorFromCssVariable('--color-highlight-background2', 0.7)} 95%,
    ${buildTransparentColorFromCssVariable('--color-highlight-background2', 0.1)}
  );
  color: inherit;

  &:before,
  &:after {
    content: ' ';
    display: inline;
    white-space: pre;
  }
`;

const StyledUl = styled.ul`
  padding-left: 1.5em;

  p + & {
    margin-top: 0.5em;
  }
`;

const StyledOl = styled.ol`
  padding-left: 1.5em;

  p + & {
    margin-top: 0.5em;
  }
`;

const StyledLi = styled.li`
  margin-bottom: 0.5em;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledHr = styled.hr`
  margin: 1.5em 0;
  width: 100%;
  max-width: 108px;
  height: 4px;
  border: none;
  border-radius: ${cornerStyleInterpolationFactory({
    cornerStyle1: '0',
    cornerStyle2: '2px',
    cornerStyle3: '2px',
  })};
  background-color: var(--color-button-primary-background);
`;

const VideoContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  border-radius: ${cornerStyleInterpolationFactory({
    cornerStyle1: '0',
    cornerStyle2: '18px',
    cornerStyle3: '40px',
  })};
`;

const StyledReactPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
`;

const clampFontFactory = (min: string, cssVariable: string, max: string) =>
  `clamp(${min}, var(${cssVariable}) * var(--global-font-factor), ${max})`;

const ClampedH5 = styled(Text.H5)`
  font-size: ${clampFontFactory('30px', '--headline5-font-size', '46px')};
`;

const ClampedH6 = styled(Text.H6)`
  font-size: ${clampFontFactory('26px', '--headline6-font-size', '32px')};
`;

const ClampedP4 = styled(Text.P4)`
  font-size: ${clampFontFactory('12px', '--paragraph4-font-size', '19px')};
`;

const DescriptionParagraph = createStyledElement('p', 'paragraph4');

const DescriptionContainer = styled.div`
  font-size: ${clampFontFactory('12px', '--paragraph4-font-size', '19px')};
`;

const ClampedL6 = styled(Text.L6)`
  font-size: ${clampFontFactory('12px', '--label6-font-size', '19px')};
`;

interface LineClampProps {
  lines: number;
}

const LineClamp = styled.div<LineClampProps>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ lines }) => lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2em;
`;

const ListItemOverline = styled(Text.L6)`
  font-size: ${clampFontFactory('8px', '--label6-font-size', '12px')};
  letter-spacing: calc(1.1 / 10 * 1em);
  text-transform: uppercase;
  font-weight: 600;
  opacity: 0.5;
`;

const ListItemHeadline = styled(ClampedH6)`
  margin-top: 0px;
  margin-bottom: 0.25em;
  max-width: 89%;
`;

const StyledMediaListItem = styled(MediaListItem)`
  justify-content: flex-start;

  @media (max-width: 800px) {
    & > div:last-child {
      margin-left: 1rem;
      padding-bottom: 1rem;
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
`;

const IconList = (props: React.SVGAttributes<SVGSVGElement>): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" {...props}>
      <g fill="none" fillRule="evenodd">
        <path
          stroke="currentColor"
          strokeWidth="2.5"
          d="m24.897 2.087.25.128a6.32 6.32 0 0 1 2.638 2.638c.715 1.337.965 2.632.965 5.83v8.634c0 3.198-.25 4.493-.965 5.83a6.32 6.32 0 0 1-2.638 2.638c-1.337.715-2.632.965-5.83.965h-8.634c-3.198 0-4.493-.25-5.83-.965a6.32 6.32 0 0 1-2.638-2.638c-.715-1.337-.965-2.632-.965-5.83v-8.634c0-3.198.25-4.493.965-5.83a6.32 6.32 0 0 1 2.638-2.638c1.337-.715 2.632-.965 5.83-.965h8.634c2.998 0 4.324.22 5.58.837Z"
        />
        <g fill="currentColor" transform="translate(7.5 8.333)">
          <rect width="10" height="3.333" x="5" rx="1.094" />
          <rect width="3.333" height="3.333" rx="1.094" />
          <rect width="3.333" height="3.333" y="5" rx="1.094" />
          <rect width="10" height="3.333" x="5" y="5" rx="1.094" />
          <rect width="3.333" height="3.333" y="10" rx="1.094" />
          <rect width="10" height="3.333" x="5" y="10" rx="1.094" />
        </g>
      </g>
    </svg>
  );
};

const SermonMetaLink = styled(NavLink)`
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const HrAlignToMarginStyle = {
  left: { marginLeft: 0, marginRight: 'auto' },
  center: { marginLeft: 'auto', marginRight: 'auto' },
  right: { marginLeft: 'auto', marginRight: 0 },
  justify: { marginLeft: 0, marginRight: 'auto' },
} satisfies Record<ThemeTextAlign, React.CSSProperties>;

const getHrAlignStyle = (align: ThemeTextAlign | undefined): React.CSSProperties =>
  HrAlignToMarginStyle[align ?? 'center'] ?? HrAlignToMarginStyle.center;

const Hr = ({ node }: { node: ThemeCustomElement }) => {
  return <StyledHr style={getHrAlignStyle(node.align)} />;
};

const Mark = (props: React.PropsWithChildren) => <StyledMark>{props.children}</StyledMark>;

const BaseBlockQuote = Text.withFontStyle('blockquote');

const StyledBlockQuote1 = styled(BaseBlockQuote)`
  position: relative;
  margin: 102px 50px 106px;
  padding-left: 44px;
  color: inherit;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: var(--color-button-primary-background);
  }
`;

const StyledBlockQuote2 = styled(BaseBlockQuote)`
  max-width: 700px;
  margin: 70px 0 98px;
  padding: 49px 75px;
  background-color: ${buildTransparentColorFromCssVariable('--color-section-text', 0.05)};
`;

interface BlockQuoteProps {
  children?: React.ReactNode;
  node: CustomElement;
}

const BlockQuote = ({ node, children }: BlockQuoteProps): JSX.Element => {
  const alignStyle = getAlignStyle(node.align);
  const fontStyle = node.fontStyle;

  if (fontStyle === 'blockquote2') {
    return (
      <StyledBlockQuote2 fontStyle={fontStyle} style={alignStyle}>
        {children}
      </StyledBlockQuote2>
    );
  }

  return (
    <StyledBlockQuote1 fontStyle={fontStyle} style={alignStyle}>
      {children}
    </StyledBlockQuote1>
  );
};

const StyledNavElement = styled(createStyledElement('nav'))`
  &[aria-label='Breadcrumb'] a[data-node-type='link'] {
    text-decoration: underline;
    opacity: 0.85;

    &:hover {
      opacity: 1;
    }
  }
`;

const NavElement = ({ node, ...props }: PropsWithNode & { children?: React.ReactNode }) => (
  <StyledNavElement node={node} aria-label={node.ariaLabel} {...props} />
);

const ElementRegistry: RichTextElementComponentMap = {
  action: ActionLink,
  card: Card,
  image: Image,
  parameter: Parameter,
  link: Link,
  'heading-one': createStyledElement('h1'),
  'heading-two': createStyledElement('h2'),
  'heading-three': createStyledElement('h3'),
  'heading-four': createStyledElement('h4'),
  'heading-five': createStyledElement('h5'),
  'heading-six': createStyledElement('h6'),
  div: createStyledElement('div'),
  nav: NavElement,
  paragraph: createStyledElement('p'),
  span: createStyledElement('span'),
  'block-quote': BlockQuote,
  'bulleted-list': createStyledElement(StyledUl),
  'numbered-list': createStyledElement(StyledOl),
  'list-item': createStyledElement(StyledLi),
  hr: Hr,
  video: Video,
  sermonPreview: Sermon,
};

const TextRegistry: RichTextCustomRichTextComponentMap = {
  bold: 'b',
  italic: 'i',
  underline: 'u',
  strike: 's',
  code: 'code',
  highlight: Mark,
};

interface RichTextContextProps {
  children: React.ReactNode;
}

export const RichTextProvider = (props: RichTextContextProps): JSX.Element => {
  return (
    <RichTextRegistryProvider registerElements={ElementRegistry} registerText={TextRegistry}>
      {props.children}
    </RichTextRegistryProvider>
  );
};
